import React, { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';

import { ENABLE_SENTRY } from '../../../_configuration';
import { IntlMsg } from '../../components/_core/IntlMsg/intl-msg.component';
import { messages } from '../../components/_core/OfflineHandler/offline-handler.messages';
import { FAILURE_SNACKBAR_MS } from '../snackbar.constants';
import { useSnackbarDismissAction } from './use-snackbar-dismiss-action';

// A handful of refusals is plausibly deliberate; a scope-wide one almost never is — it means a
// role, an age group or an assessment moved under a scorer mid-session. Worth waking someone.
export const REFUSAL_ALARM_THRESHOLD = 10;

// Tells the user the server refused some scores. The refused actions are still deleted like any
// saved action; this is only the notice. Call once per submit or sync pass, with the pass's total.
export const useRefusedScoresSnackbar = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dismissAction = useSnackbarDismissAction();

  // One shared reason is named. Several distinct ones would not fit a snackbar, and a server that
  // sends none — an older one, or a path with no reason — keeps the generic line.
  return useCallback((count: number, reasons: string[] = []) => {
    if (count <= 0) return;
    const reason = reasons.length === 1 ? reasons[0] : undefined;

    // Retaining these instead would not save them: both submit paths send skipDuplicateActions,
    // and the server counts a stored REFUSED row as terminal, so the retry is answered DUPLICATE
    // and the device deletes anyway. The rows are recoverable server-side, so raise the alarm.
    if (count >= REFUSAL_ALARM_THRESHOLD) {
      if (ENABLE_SENTRY) Sentry.captureMessage(`Mass score refusal: ${count} scores`, { extra: { count, reasons } } as never);
      enqueueSnackbar(
        <IntlMsg {...(reason ? messages.scoresRefusedManyWithReason : messages.scoresRefusedMany)} values={{ count, reason }} />,
        { variant: 'error', persist: true, action: dismissAction },
      );
      return;
    }

    const message = reason
      ? <IntlMsg {...messages.scoresRefusedWithReason} values={{ count, reason }} />
      : <IntlMsg {...messages.scoresRefused} values={{ count }} />;
    enqueueSnackbar(message, { variant: 'warning', autoHideDuration: FAILURE_SNACKBAR_MS, action: dismissAction });
  }, [enqueueSnackbar, dismissAction]);
};
